/**
 * `ProjectBriefDetail` — editable, auto-saving brief detail page
 * (MFINS-2519 Phase 2).
 *
 * Wires the brief query into an editable surface composed of three hooks:
 *   1. `useBriefEditorForm` — owns title / values / sections + dirty +
 *      validity + a monotonic `changeCounter`. Seeded ONCE from the query
 *      response thanks to the `key={brief.id}` remount on `BriefEditorSurface`
 *      (Rule 5 — reset with key, not effect choreography).
 *   2. `useBriefAutoSave` — debounces the counter and dispatches the
 *      auto-save PATCH. R13 — the counter only ticks on title/value edits,
 *      so section edits never trigger auto-save (avoids BE replace-all of
 *      section UUIDs, which would destroy TipTap cursor state).
 *   3. `useUpdateBrief` — wraps the mutation. On success it rebases the
 *      detail cache via `setQueryData` (no refetch flicker — R10) and
 *      invalidates the project lists key for derived-field staleness.
 *
 * Layout: two-column flex container per design obs #2075.
 *   - LHS `brief-editor-column` (flex-1) — header + fields + section editors
 *   - RHS — `BriefRightPanel`, a collapsible `DetailSidebar` hosting the
 *     `BriefCommentsSidebar` body under the Comments tab and the Info content
 *     under the Info tab. Info data is partially mocked.
 *
 * Title editing: lives inline in the header (decision per orchestrator
 * prompt — title is part of the brief shell, not a `values` field). The
 * header receives `title` + `onTitleChange` and replaces its breadcrumb
 * leaf with an editable input styled to blend with the breadcrumb text.
 *
 * NO React effect hook in this module — every behavior is render-from-state
 * or an event handler. The mutation success/error callbacks ARE events;
 * the `key={brief.id}` remount handles seed-on-navigation.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import type { BriefResponseDto, UpdateBriefDto } from '@inference.engine/workspace-api-client/web';
import type { BriefManualSavePayload } from './hooks/useBriefEditorForm/useBriefEditorForm';
import { Spinner } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import type { Content, Editor } from '@tiptap/react';

import {
  briefByIdQueryOptions,
  downloadBriefMarkdown,
  useDuplicateBrief,
  useExportBriefPdf,
  useUpdateBrief,
} from '@/entities/brief';
import type { ProjectDetail } from '@/entities/project';
import { useBriefAgentReview } from '@/features/brief-agent-review';
import { EditBriefModal } from '@/features/edit-brief';
import {
  useBriefCommentAnnotations,
  buildBriefAnnotationUpdates,
  useInvalidateBriefCommentCaches,
  usePendingFieldAnchor,
  useBriefSectionCommentIndex,
} from '@/features/comment-on-file';
import { notificationService } from '@/lib/services/notification.service';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import { useDetailSidebar } from '@/shared/ui';
import { useHideAppSidebar } from '@/widgets/app-shell';
import { ROUTES } from '@/shared/config/routes';

import { BriefDetailError } from './components/BriefDetailError';
import { BriefDetailHeader } from './components/BriefDetailHeader';
import { BriefDetailLoading } from './components/BriefDetailLoading';
import { BriefRightPanel } from './components/BriefRightPanel';
import { MarkBriefReadyDialog } from './components/MarkBriefReadyDialog';
import { BriefSectionField } from './components/BriefSectionField';
import type { SaveState } from './components/BriefSaveIndicator';
import { useBriefAutoSave } from './hooks/useBriefAutoSave';
import { useBriefEditorForm } from './hooks/useBriefEditorForm';
import { useReconcileBriefTemplate } from './hooks/useReconcileBriefTemplate';
import { deriveSubmitDisabledReason } from './lib/deriveSubmitDisabledReason';
import { useTranslation } from 'react-i18next';
import { resolveSuggestion } from './lib/resolveSuggestion';

const BRIEF_PANEL_WIDTH_KEY = 'brief-comments-panel-width';

export function ProjectBriefDetail() {
  const { briefId = '' } = useParams<{ briefId: string }>();
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const platformProjectId = project.platformProjectId;

  useHideAppSidebar();

  const briefQuery = useQuery(briefByIdQueryOptions({ projectId: platformProjectId, briefId }));

  const { refetch } = briefQuery;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (briefQuery.isPending) {
    return <BriefDetailLoading />;
  }

  if (!briefQuery.isSuccess) {
    return <BriefDetailError onRetry={handleRetry} />;
  }

  return (
    <BriefEditorSurface
      key={briefQuery.data.id}
      brief={briefQuery.data}
      platformProjectId={platformProjectId}
      briefId={briefId}
    />
  );
}

interface BriefEditorSurfaceProps {
  brief: BriefResponseDto;
  platformProjectId: string;
  briefId: string;
}

/**
 * Inner editor surface. Lives inside its own component so the `useState`
 * initializer in `useBriefEditorForm` re-runs when the route changes to a
 * different brief id (`key={brief.id}` on the parent's render). All page
 * wiring lives here.
 */
function BriefEditorSurface({ brief, platformProjectId, briefId }: BriefEditorSurfaceProps) {
  const { t } = useTranslation('projects');

  // Scope for the brief comments sidebar. ProjectBriefDetail renders inside the
  // ProjectDetail outlet, so the loaded project (with workspaceId +
  // platformProjectId) is available via outlet context — same source the asset
  // detail page uses to seed the comment store.
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();

  const form = useBriefEditorForm({ brief });

  const { exportPdf, isExporting: isExportingPdf } = useExportBriefPdf({
    projectId: platformProjectId,
    briefId,
    briefTitle: brief.title,
  });
  const handleDownloadPdf = useCallback(() => {
    void exportPdf();
  }, [exportPdf]);

  const handleDownloadMarkdown = useCallback(() => {
    downloadBriefMarkdown(brief);
  }, [brief]);

  // Rename dialog (`EditBriefModal`). The detail-cache rebase in `useUpdateBrief`
  // refreshes the page-header breadcrumb (`brief.title`) on its own, but the
  // editor form holds its own once-seeded title copy — sync it on success so
  // the editor card heading updates AND the next auto-save doesn't re-PATCH the
  // stale title back (clobbering the rename).
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const handleEdit = useCallback(() => setIsRenameOpen(true), []);
  const handleRenamed = useCallback(
    (updated: BriefResponseDto) => form.syncTitle(updated.title),
    [form],
  );

  // Duplicate from the detail page surfaces a toast with an "Open copy" link to
  // the new brief (vs. the row menu's plain default toast — see useDuplicateBrief).
  const duplicateMutation = useDuplicateBrief({
    projectId: platformProjectId,
    briefId,
    onSuccess: (created) => {
      notificationService.showSuccess(t('projectBriefDetail.briefDuplicated'), {
        action: {
          label: t('projectBriefDetail.openCopy'),
          onClick: () => void navigate(ROUTES.PROJECT_BRIEF_DETAIL(platformProjectId, created.id)),
        },
      });
    },
    onError: (err) => {
      notificationService.showError(t('projectBriefDetail.failedToDuplicateBrief'), {
        description: err.message,
      });
    },
  });
  const handleDuplicate = useCallback(() => {
    if (duplicateMutation.isPending) return;
    duplicateMutation.mutate();
  }, [duplicateMutation]);

  // Reconcile adds sections, retires them in place, and empties an answer the refreshed field no
  // longer accepts. The form was seeded once, so it is merged against the refetched brief rather
  // than re-seeded from it: an edit in flight is worth more than a tidy reset, but an answer the
  // server has rejected cannot be kept or the next Save 400s. `retired`, `type` and `config` are
  // read from the query's brief at render, so that half needs nothing here.
  const { adoptTemplateSections } = form;
  const { reconcile, isReconciling } = useReconcileBriefTemplate({
    projectId: platformProjectId,
    briefId,
    sections: brief.sections,
    onSectionsAdopted: adoptTemplateSections,
  });

  // `savedAt` is a tiny piece of UI state: when we last got a successful
  // save back. Updated in the mutation's `onSuccess` event handler (NOT in
  // an effect — Rule 3).
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Pinned to the last-focused editor — NOT cleared on blur. Clicking a
  // toolbar button steals DOM focus from the editor; clearing on blur
  // would disable the toolbar before its own click handler runs.
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const handleEditorFocus = useCallback((editor: Editor) => setActiveEditor(editor), []);

  // Live editor instance per section, so an agent suggestion can be applied
  // into the right section's editor as an undoable transaction.
  const sectionEditorsRef = useRef<Map<string, Editor>>(new Map());
  const registerEditor = useCallback((sectionId: string, editor: Editor | null) => {
    if (editor) sectionEditorsRef.current.set(sectionId, editor);
    else sectionEditorsRef.current.delete(sectionId);
  }, []);

  const sidebar = useDetailSidebar<'comments' | 'info'>({
    widthStorageKey: BRIEF_PANEL_WIDTH_KEY,
    defaultView: 'comments',
  });

  // Initiating a comment or highlight must surface the composer, so this opens
  // the Comments view outright rather than toggling: the rail may already be up
  // on Comments, and toggling would close it.
  const { openView } = sidebar;
  const handleAddCommentInitiated = useCallback(() => {
    openView('comments');
  }, [openView]);

  const { setPendingFieldAnchor, clearPendingFieldAnchor } = usePendingFieldAnchor();
  // The rail's re-seed keeps an anchor made for this brief, so one abandoned here would come
  // back on the next visit. Not in the store's reset: the rail runs that on every close, which
  // falls between "Comment on section" and the re-seed that must keep the anchor.
  useMountEffect(() => () => clearPendingFieldAnchor());
  const handleCommentOnField = useCallback(
    (sectionId: string) => {
      setPendingFieldAnchor({ sectionId, targetId: briefId });
      openView('comments');
    },
    [setPendingFieldAnchor, openView, briefId],
  );

  // Agent brief review (POC) — fires on explicit Save only (per-call mutate
  // callback below), NOT in the shared `useUpdateBrief` onSuccess, which the
  // title/values auto-save also routes through.
  const { review, isReviewing } = useBriefAgentReview();

  // Captures the payload submitted for the in-flight request so the
  // success handler can rebaseline against EXACTLY what was sent — not
  // against the server's response and not against the current form state
  // (which may include user edits made while the request was in flight).
  // This is the fix for the auto-save lost-update race: see F1 in
  // PR #2562 review and `useBriefEditorForm.rebaseline` docs.
  const inFlightPayloadRef = useRef<BriefManualSavePayload | null>(null);

  const handleSuccess = useCallback(() => {
    const submitted = inFlightPayloadRef.current;
    inFlightPayloadRef.current = null;
    if (submitted) {
      form.rebaseline(submitted);
    }
    setSavedAt(new Date());
  }, [form]);

  const handleError = useCallback(
    (err: Error) => {
      inFlightPayloadRef.current = null;
      notificationService.showError(t('projectBriefDetail.failedToSaveBrief'), {
        description: err.message,
      });
    },
    [t],
  );

  const mutation = useUpdateBrief({
    projectId: platformProjectId,
    briefId,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // "Mark as ready" no longer transitions from here: the dialog owns the
  // transition and the approval folder it may create alongside it, because the
  // two are one user decision and only it knows which way that decision went.
  const [isMarkReadyOpen, setIsMarkReadyOpen] = useState(false);
  const handleSubmit = useCallback(() => setIsMarkReadyOpen(true), []);
  const handleMarkReadyClose = useCallback(() => setIsMarkReadyOpen(false), []);

  // Auto-save dispatcher — pulls the latest auto-save payload (title +
  // values; R13 excludes sections) at fire time and hands it to the
  // mutation. The hook's debounce window is owned inside the hook.
  // Stamps the in-flight ref with the FULL shape (sections = current
  // sections at dispatch time) so the rebaseline mirror stays accurate.
  const handleAutoSave = useCallback(
    (body: { title: string; values: Record<string, unknown> }) => {
      inFlightPayloadRef.current = {
        title: body.title,
        values: body.values,
        sections: form.sections.map((s) => ({
          id: s.id,
          name: s.name,
          position: s.position,
          content: s.content,
          value: s.value ?? null,
        })),
      };
      mutation.mutate({ body });
    },
    [mutation, form.sections],
  );

  useBriefAutoSave({
    changeCounter: form.changeCounter,
    isValid: form.isValid,
    isPending: mutation.isPending,
    getPayload: form.getSubmitPayload,
    save: handleAutoSave,
  });

  const briefAnnotations = useBriefCommentAnnotations(platformProjectId, briefId);
  // Counts cover both anchor kinds, so a section commented on only through its own affordance
  // still shows that a discussion exists.
  const { countsBySection } = useBriefSectionCommentIndex(platformProjectId, briefId);
  const refreshBriefCommentCaches = useInvalidateBriefCommentCaches(platformProjectId, briefId);

  // Applying an agent suggestion inserts the content into the section's LIVE
  // editor (selectAll + insertContent) so it lands in that editor's undo
  // history — the toolbar's undo/redo then reverts it like any manual edit, and
  // the editor's onUpdate lifts it into the form (dirty → persisted on Save).
  // Fallback (rare: the section editor isn't mounted): apply via the form and
  // remount the section so the content is at least visible (not undoable).
  const [sectionContentRevisions, setSectionContentRevisions] = useState<Record<string, number>>(
    {},
  );
  // Which typed sections currently hold a value an agent wrote, and what each one held before
  // it did. `BriefSectionResponseDto` carries no `source` field, so provenance cannot be
  // persisted; it lives for the life of the page, which is exactly as long as the choice to
  // accept, edit or clear is still open. A reload leaves the value in place, unmarked, because
  // by then it has either been saved deliberately or lost with the rest of the unsaved edits.
  const [aiWrittenValues, setAiWrittenValues] = useState<Record<string, { previous: unknown }>>({});

  const dropAiMark = useCallback((sectionId: string) => {
    setAiWrittenValues((prev) =>
      sectionId in prev
        ? Object.fromEntries(Object.entries(prev).filter(([id]) => id !== sectionId))
        : prev,
    );
  }, []);

  const handleSuggestionApplied = useCallback(
    (sectionId: string, content: Record<string, unknown>): boolean => {
      // A typed section has no editor to insert into, and its value must clear the field's own
      // constraint first. A suggestion the field cannot hold is refused OUT LOUD rather than
      // dropped: a value that silently fails to appear reads as the feature being broken.
      //
      // Returns whether anything was actually written, so the caller can tell refusal from
      // success. Reporting nothing let a refused suggestion be resolved as "applied" and
      // acknowledged with a success toast, directly on top of the warning.
      const target = brief.sections.find((s) => s.id === sectionId);
      if (target?.type && target.type !== 'text') {
        const resolved = resolveSuggestion({ type: target.type, config: target.config }, content);
        if (resolved.kind === 'rejected') {
          notificationService.showWarning(t('projectBriefDetail.suggestionOutsideFieldOptions'));
          return false;
        }
        if (resolved.kind === 'value') {
          // Remembered BEFORE the write, so Clear has something to put back. An existing mark
          // is kept rather than overwritten: two suggestions in a row must still rewind to
          // what the briefer had, not to the first suggestion.
          setAiWrittenValues((prev) =>
            sectionId in prev
              ? prev
              : {
                  ...prev,
                  [sectionId]: {
                    previous: form.sections.find((s) => s.id === sectionId)?.value ?? null,
                  },
                },
          );
          // `setSectionValue`, not `applySectionValue`: the latter moves the baseline with the
          // value, which leaves the brief undirty and the Save button disabled, so a suggestion
          // would show on screen and never reach the server.
          form.setSectionValue(sectionId, resolved.value);
          return true;
        }
      }

      const editor = sectionEditorsRef.current.get(sectionId);
      if (editor) {
        // `content` is a Tiptap doc; insert its child nodes so selectAll's
        // whole-doc selection is replaced cleanly in a single history step.
        const inner = (content as { content?: unknown }).content;
        const replacement = (Array.isArray(inner) ? inner : content) as Content;
        editor.chain().focus().selectAll().insertContent(replacement).run();
        return true;
      }
      form.applySectionContent(sectionId, content);
      setSectionContentRevisions((prev) => ({ ...prev, [sectionId]: (prev[sectionId] ?? 0) + 1 }));
      return true;
    },
    [form, brief, t],
  );

  // Manual save bypasses the debounce and ships the FULL payload including
  // sections — the user explicitly accepted the round trip (R13). Null
  // section content is coerced to `undefined` so the SDK DTO accepts it.
  const handleManualSave = useCallback(() => {
    if (!form.isValid) return;
    const full = form.getFullSubmitPayload();
    inFlightPayloadRef.current = {
      title: full.title,
      values: full.values,
      sections: full.sections.map((s) => ({
        id: s.id,
        name: s.name,
        position: s.position,
        content: s.content,
        value: s.value ?? null,
      })),
    };
    // Diff the live (reflowed) highlight ranges against the loaded annotations
    // and ship the reposition/detach changes WITH the content save — one PATCH,
    // atomic, authorized by brief write (covers every user's anchors).
    const commentAnnotations = buildBriefAnnotationUpdates(briefAnnotations);
    const body: UpdateBriefDto = {
      title: full.title,
      values: full.values,
      sections: full.sections.map((s) => ({
        id: s.id,
        name: s.name,
        position: s.position,
        content: s.content ?? undefined,
        // Unlike `content`, `null` here is a real, meaningful signal ("user
        // cleared the field") that must reach the API as `null`, not be
        // coerced to `undefined` (which the backend reads as "omitted —
        // preserve the existing value"). The section's `value` is `unknown`
        // by design (its shape depends on `type`), so the cast narrows it to
        // what the generated DTO accepts without widening the form's typing.
        value: (s.value ?? null) as Record<string, unknown> | null,
      })),
      ...(commentAnnotations.length > 0 ? { commentAnnotations } : {}),
    };
    mutation.mutate(
      { body },
      {
        onSuccess: (fresh) => {
          if (commentAnnotations.length > 0) refreshBriefCommentCaches();
          void review(fresh, platformProjectId);
        },
      },
    );
  }, [form, mutation, review, platformProjectId, briefAnnotations, refreshBriefCommentCaches]);

  // Derive the save state for the indicator from the mutation flags +
  // form dirty + `savedAt`. Priority: in-flight save > error > unsaved (dirty
  // edits not yet persisted) > saved (last save succeeded) > idle. `savedAt`
  // is set in the success event handler. No effect choreography needed.
  const saveState: SaveState = mutation.isPending
    ? 'saving'
    : mutation.isError
      ? 'error'
      : form.isDirty
        ? 'unsaved'
        : savedAt
          ? 'saved'
          : 'idle';

  const sortedSections = useMemo(
    () => [...form.sections].sort((a, b) => a.position - b.position),
    [form.sections],
  );

  const submitDisabledReason = useMemo(
    () =>
      deriveSubmitDisabledReason({
        status: brief.status,
        isValid: form.isValid,
        isDirty: form.isDirty,
        isUpdatePending: mutation.isPending,
      }),
    [brief.status, form.isValid, form.isDirty, mutation.isPending],
  );

  // BriefSectionEditor emits TipTap JSON as `unknown`; the form expects
  // `Record<string, unknown>`. Narrow at the boundary so the form stays
  // strictly typed and TipTap's loose `getJSON()` return doesn't leak.
  const handleSectionChange = useCallback(
    (sectionId: string, content: unknown) => {
      form.setSectionContent(sectionId, content as Record<string, unknown>);
    },
    [form],
  );

  // An edit through the control is the third way out of a suggestion: once the briefer has
  // touched the value it is theirs, so the mark goes without them having to accept as well.
  const handleSectionValueChange = useCallback(
    (sectionId: string, value: unknown) => {
      form.setSectionValue(sectionId, value);
      dropAiMark(sectionId);
    },
    [form, dropAiMark],
  );

  // Accepting only drops the mark: the value is already in the form as an unsaved edit, so
  // standing behind it is exactly the act of no longer calling it generated. Clearing carries
  // the remembered value with it rather than looking it up, so it cannot go missing.
  const handleClearAiValue = useCallback(
    (sectionId: string, previous: unknown) => {
      form.setSectionValue(sectionId, previous);
      dropAiMark(sectionId);
    },
    [form, dropAiMark],
  );

  const aiSuggestionActions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(aiWrittenValues).map(([sectionId, { previous }]) => [
          sectionId,
          {
            onAccept: () => dropAiMark(sectionId),
            onClear: () => handleClearAiValue(sectionId, previous),
          },
        ]),
      ),
    [aiWrittenValues, dropAiMark, handleClearAiValue],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-app-inset">
      <BriefDetailHeader
        brief={brief}
        platformProjectId={platformProjectId}
        sidebar={sidebar}
        onSave={handleManualSave}
        onRetrySave={handleManualSave}
        saveState={saveState}
        savedAt={savedAt}
        saveError={mutation.error?.message ?? null}
        isDirty={form.isDirty}
        isValid={form.isValid}
        isSaving={mutation.isPending}
        onSubmit={handleSubmit}
        submitDisabledReason={submitDisabledReason}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        isDuplicating={duplicateMutation.isPending}
        onDownloadPdf={handleDownloadPdf}
        isExportingPdf={isExportingPdf}
        onDownloadMarkdown={handleDownloadMarkdown}
        onReconcileTemplate={reconcile}
        isReconciling={isReconciling}
      />

      <MarkBriefReadyDialog
        open={isMarkReadyOpen}
        brief={brief}
        platformProjectId={platformProjectId}
        onClose={handleMarkReadyClose}
      />

      <EditBriefModal
        open={isRenameOpen}
        projectId={platformProjectId}
        briefId={briefId}
        currentTitle={brief.title}
        onClose={() => setIsRenameOpen(false)}
        onSuccess={handleRenamed}
      />

      {/* No `gap-4` here: the resize handle owns a 16px track that recreates
          the original editor↔panel spacing, so a row gap would double it. */}
      <div className="flex min-h-0 flex-1">
        {/* Editor card — flex-1. Composes: title bar (top) + sticky toolbar
            + scrollable content. Per Figma node 6053:20670. */}
        <div
          data-testid="brief-editor-column"
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card"
        >
          {/* Title centered with the toggle pinned to the right, so the
              card header reads as a balanced row even when the panel is
              collapsed. Vertical padding matches the DetailSidebar header
              beside it, so the two bottom borders line up, which is also why
              the title truncates instead of wrapping. The equal outer tracks
              keep it centred; the right one never shrinks below the
              reviewing indicator, so a long title stops short of it. */}
          <header
            data-testid="brief-editor-card-header"
            className="grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-2 border-b border-border-light px-xl py-md"
          >
            <h2 className="col-start-2 min-w-0 truncate text-base font-semibold text-foreground">
              {form.title}
            </h2>
            <div className="-mr-3 flex items-center gap-2 justify-self-end">
              {isReviewing && (
                <span
                  data-testid="brief-agents-reviewing"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <Spinner className="size-3.5" />
                  {t('projectBriefDetail.agentsReviewing')}
                </span>
              )}
            </div>
          </header>

          {/*
            The document reads as a centred, constrained column rather than full-bleed text.
            The left padding is the track the per-section format bar outdents into, so the bar
            sits outside the text without being clipped by the scroll container.
          */}
          <div className="flex min-h-0 flex-1 flex-col overflow-auto p-xl">
            <div
              data-testid="brief-document-column"
              className="mx-auto flex w-full max-w-[760px] flex-col gap-xl pl-5"
            >
              {sortedSections.map((section) => {
                // BriefSectionField expects the SDK shape; flags (`required`,
                // `aiAllowed`, `type`, `config`, ...) live on the brief response
                // only — pull them back from the seeded `brief.sections` so the
                // type stays satisfied. `value` comes from live form state.
                const sourceSection = brief.sections.find((s) => s.id === section.id);
                return (
                  // R11 — key on `section.id` (so TipTap survives content
                  // updates from save success rebase) + the apply revision,
                  // which bumps ONLY when an agent suggestion replaces this
                  // section's content and the editor must remount to show it.
                  <BriefSectionField
                    key={`${section.id}:${(sectionContentRevisions[section.id] ?? 0).toString()}`}
                    section={{
                      id: section.id,
                      briefId,
                      name: section.name,
                      position: section.position,
                      required: sourceSection?.required ?? false,
                      aiAllowed: sourceSection?.aiAllowed ?? true,
                      audienceIds: sourceSection?.audienceIds ?? [],
                      type: sourceSection?.type ?? null,
                      config: sourceSection?.config ?? {},
                      allowFiles: sourceSection?.allowFiles ?? false,
                      aiInstructions: sourceSection?.aiInstructions ?? null,
                      templateSectionId: sourceSection?.templateSectionId ?? null,
                      retired: sourceSection?.retired ?? false,
                      createdAt: brief.createdAt,
                      updatedAt: brief.updatedAt,
                      content: section.content as never,
                      value: section.value as never,
                    }}
                    onContentChange={handleSectionChange}
                    onValueChange={handleSectionValueChange}
                    onEditorFocus={handleEditorFocus}
                    registerEditor={registerEditor}
                    onAddCommentInitiated={handleAddCommentInitiated}
                    onHighlightClick={handleAddCommentInitiated}
                    onCommentOnField={handleCommentOnField}
                    activeEditor={activeEditor}
                    commentCount={countsBySection[section.id] ?? 0}
                    onOpenComments={handleAddCommentInitiated}
                    annotations={briefAnnotations}
                    aiSuggestion={aiSuggestionActions[section.id]}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Hosts the Comments and Info views. Info data sources are partially
            mocked until the BE threads workspace members to briefs. */}
        <BriefRightPanel
          brief={brief}
          projectId={platformProjectId}
          workspaceId={project.workspaceId}
          platformProjectId={project.platformProjectId}
          view={sidebar.view}
          onSuggestionApplied={handleSuggestionApplied}
          isSaving={mutation.isPending}
          width={sidebar.width}
          onResize={sidebar.setWidth}
          onResizeEnd={sidebar.commitWidth}
        />
      </div>
    </div>
  );
}
